/**
 * What the app shows instead of the shell online when a person cannot work yet (decisions-log
 * W8, W11): signed out after a failed login, waiting for a role, or blocked.
 */
import type { MeResponse } from '@outsource/shared/auth/me';
import { useEffect } from 'react';
import { signInAgain } from '../../lib/api';
import { Button } from '../ui/Button';
import { signOut } from './UserMenu';

/** `?login=…` set by /auth/callback when Keycloak's answer is refused. */
const LOGIN_REFUSALS: Readonly<Record<string, string>> = {
  no_role: 'В Keycloak у вашей учётной записи нет доступа к этому приложению. Попросите администратора выдать его.',
  email_taken: 'Эта почта уже привязана к другой учётной записи. Обратитесь к владельцу.',
  no_email: 'В учётной записи Keycloak не указана почта.',
  expired: 'Вход занял слишком много времени. Попробуйте ещё раз.',
  failed: 'Не удалось войти через Keycloak. Попробуйте ещё раз.',
};

function Card({ title, text, children }: { readonly title: string; readonly text: string; readonly children: React.ReactNode }) {
  return (
    <main className="grid min-h-screen place-items-center bg-bg-2 p-6">
      <div role="alert" className="flex w-full max-w-[420px] flex-col gap-3 rounded-3 border border-border-1 bg-bg-1 p-6">
        <h1 className="text-[18px] font-semibold text-fg-1">{title}</h1>
        <p className="text-[14px] text-fg-2">{text}</p>
        <div className="flex justify-end gap-2">{children}</div>
      </div>
    </main>
  );
}

/** Signed out: straight to Keycloak, unless the last login failed (then say why, no loop). */
export function SignInScreen() {
  const reason = new URLSearchParams(window.location.search).get('login');
  useEffect(() => {
    if (!reason) signInAgain();
  }, [reason]);
  if (!reason) return <Card title="Переход ко входу…" text="Открываем вход через Keycloak." >{null}</Card>;
  return (
    <Card title="Войти не получилось" text={LOGIN_REFUSALS[reason] ?? LOGIN_REFUSALS.failed!}>
      <Button onClick={() => window.location.assign('/auth/login')}>Войти</Button>
    </Card>
  );
}

export function NoAccessScreen({ me }: { readonly me: MeResponse }) {
  const isBlocked = me.user?.status === 'blocked';
  const title = isBlocked ? 'Доступ закрыт' : 'Доступ ещё не выдан';
  const text = isBlocked
    ? `Учётная запись ${me.user?.email ?? ''} заблокирована. Обратитесь к владельцу.`
    : `Вы вошли как ${me.user?.email ?? ''}. Владелец ещё не назначил вам роль — после этого обновите страницу.`;
  return (
    <Card title={title} text={text}>
      <Button variant="secondary" onClick={() => void signOut()}>
        Выйти
      </Button>
    </Card>
  );
}
